import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

function Navbar() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const [atTop, setAtTop] = useState(true);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // Common conditions
  const isTransparentMode = atTop  && isDesktop;

  // Animation variants
  const menuVariants = {
    closed: {
      x: "-100%",
      opacity: 0,
      transition: { duration: 0.2 },
    },
    open: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        staggerChildren: 0.1,
        when: "beforeChildren",
      },
    },
  };

  const menuItemVariants = {
    closed: { x: -20, opacity: 0 },
    open: { x: 0, opacity: 1 },
  };

  // Screen size tracking
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll tracking
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      setAtTop(currentScrollPos < 10);
      setVisible(prevScrollPos > currentScrollPos || currentScrollPos < 10);
      setPrevScrollPos(currentScrollPos);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollPos]);

  // Styles
  const navbarStyle = {
    transform: visible ? "translateY(0)" : "translateY(-100%)",
    transition: "transform 0.1s ease, background-color 0.1s ease",
    position: "fixed",
    top: -3,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: isTransparentMode ? "transparent" : "#222222",
  };

  const getNavLinkStyle = (isActive) => ({
    color: isActive
      ? isTransparentMode
        ? "#a41e24"
        : "#e9b420"
      : isTransparentMode
      ? "#000"
      : "#fff",
    fontFamily: "Veneer, sans-serif",
    fontSize: 25,
  });

  const getUnderlineStyle = () => ({
    background: isTransparentMode ? "#a41e24" : "#e9b420",
  });

  const renderNavLink = (item) => {
    const path = item === "Home" ? "/" : `/${item.toLowerCase()}`;
    const isActive = location.pathname === path;

    return (
      <Link key={item} to={path} className="relative group px-3 py-1">
        <span
          style={getNavLinkStyle(isActive)}
          className="text-base font-medium group-hover:text-orange-300 transition-colors duration-300"
        >
          {item}
        </span>
        {isActive && (
          <motion.div
            layoutId="underline"
            style={getUnderlineStyle()}
            className="absolute bottom-0 left-0 w-full h-0.5"
            initial={false}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30,
            }}
          />
        )}
      </Link>
    );
  };

  const renderMobileNavLink = (item) => {
    const path = item === "Home" ? "/" : `/${item.toLowerCase()}`;
    const isActive = location.pathname === path;

    return (
      <motion.div key={item} variants={menuItemVariants}>
        <Link
          to={path}
          className="block px-3 py-2 font-extrabold rounded-lg hover:bg-gray-800 transition-colors"
          onClick={() => setIsOpen(false)}
        >
          <span
            style={{
              color: isActive ? "var(--primary-yellow)" : "#D1D5DB",
            }}
            className="text-lg font-extrabold"
          >
            {item}
          </span>
        </Link>
      </motion.div>
    );
  };

  return (
    <div className="w-full" style={navbarStyle}>
      <nav className="relative">
        <div
          className={`max-w-7xl mx-auto px-4 ${
            isTransparentMode ? "py-0" : "py-2"
          }`}
        >
          <div className="flex items-center justify-between h-16">
            {/* Mobile menu button */}
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden text-gray-300 hover:text-white transition-colors"
            >
              <Menu size={24} />
            </button>

            {/* Logo */}
            <div className="flex-shrink-0">
              <Link
                to="/"
                className="flex items-center hover:opacity-80 transition-opacity"
              >
                <div
                  className={` relative  rounded-b-xl ${
                    isTransparentMode ? "bg-[#a41e24] w-[200px] h-[100px]" : "bg-[#222222] w-[150px] h-[80px]"
                  }`}
                >
                  <img
                    src="/media/zibvio-logo-no-bg.svg"
                    alt="Product"
                    className={`w-[200px]  mt-[5px] absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 ${
                      isTransparentMode ? "pt-3" : "pt-0"
                    }`}
                  />
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-center flex-1">
              <div className="flex space-x-12 relative">
                {["Home", "Shop", "FAQs"].map(renderNavLink)}
              </div>
            </div>

            {/* Contact Button */}
            {isDesktop && (
              <Link
                to="/contact"
                style={{ background: "var(--gradient-primary)" }}
                className="text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Contact us
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
              className="fixed inset-0 bg-[#222222] lg:hidden z-[9999] w-full h-screen"
            >
              <div className="p-4 flex justify-between items-center bg-[#222222] border-b border-gray-800">
                <Link
                  to="/"
                  style={{
                    background: "var(--gradient-primary)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                  className="text-2xl font-bold"
                  onClick={() => setIsOpen(false)}
                >
                  <img
                    src="/media/zibvio-logo-no-bg.svg"
                    alt="Product"
                    className="w-[100px]"
                  />
                </Link>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <motion.div className="flex flex-col space-y-4 p-4">
                {["Home", "Shop", "FAQs"].map(renderMobileNavLink)}
                <motion.div variants={menuItemVariants}>
                  <Link
                    to="/contact"
                    style={{ background: "var(--gradient-primary)" }}
                    className="block text-center text-white px-4 py-2 rounded-lg text-lg font-medium hover:opacity-90 transition-opacity"
                    onClick={() => setIsOpen(false)}
                  >
                    Contact us
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>
  );
}

export default Navbar;
